import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSession } from "@/platform/auth";
import { useAuth } from "@/platform/auth";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { ShieldCheck, CircleAlert, CircleCheck } from "lucide-react";
import { Link, useSearchParams } from "react-router";
import type { EmailOtpType } from "@supabase/supabase-js";

type ConfirmFormValues = { password: string; password2: string };

export function ResetPasswordConfirmPage() {
  const { isAuthenticated, isLoading } = useSession();
  const { updatePassword, signInWithOTP } = useAuth();

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const type = searchParams.get("type") as EmailOtpType;

  useEffect(() => {
    const verifyToken = async () => {
      if (token && type && !isAuthenticated) {
        await signInWithOTP(token, type);
      }
    };
    void verifyToken();
  }, [token, type, isAuthenticated, signInWithOTP]);

  const [updated, setUpdated] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
    getValues,
  } = useForm<ConfirmFormValues>({
    defaultValues: { password: "", password2: "" },
  });

  const onSubmit = async ({ password }: ConfirmFormValues) => {
    clearErrors("root");
    const { error } = await updatePassword(password);
    if (error) setError("root", { type: "server", message: error });
    else setUpdated(true);
  };

  return (
    <>
      <title>Confirm Password Reset - Researchly</title>
      <div className="flex min-h-[70vh] items-center justify-center p-4">
        <div className="w-full max-w-md rounded-xl border p-6">
          <h1 className="mb-1 text-2xl font-semibold">Set a new password</h1>
          <p className="mb-6 text-sm text-muted-foreground">
            Choose a strong password for your account.
          </p>

          {updated ? null : isLoading ? (
            <p className="text-sm text-muted-foreground">
              Verifying your link…
            </p>
          ) : !isAuthenticated ? (
            <p className="text-sm text-muted-foreground">
              This page is only accessible via a valid password reset link.
              Please request a new link from the{" "}
              <Link to="/reset-password" className="underline text-primary">
                reset page
              </Link>
            </p>
          ) : (
            <form
              className="space-y-3"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <label className="text-sm font-medium" htmlFor="password">
                New password
              </label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                aria-invalid={!!errors.password}
                aria-describedby={
                  errors.password ? "password-error" : undefined
                }
                {...register("password", { required: "Password is required" })}
              />
              {errors.password && (
                <p
                  id="password-error"
                  className="text-xs text-destructive"
                  aria-live="polite"
                >
                  {errors.password.message}
                </p>
              )}
              <label className="text-sm font-medium" htmlFor="password2">
                Confirm password
              </label>
              <Input
                id="password2"
                type="password"
                autoComplete="new-password"
                aria-invalid={!!errors.password2}
                aria-describedby={
                  errors.password2 ? "password2-error" : undefined
                }
                {...register("password2", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === getValues("password") || "Passwords do not match",
                })}
              />
              {errors.password2 && (
                <p
                  id="password2-error"
                  className="text-xs text-destructive"
                  aria-live="polite"
                >
                  {errors.password2.message}
                </p>
              )}
              <Button
                type="submit"
                className="w-full"
                variant="default"
                disabled={isSubmitting}
              >
                <ShieldCheck className="mr-2 h-4 w-4" /> Update password
              </Button>
            </form>
          )}

          {errors.root && (
            <Alert variant="destructive" className="mt-3">
              <CircleAlert />
              <AlertTitle>Update failed</AlertTitle>
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}
          {updated && (
            <>
              <Alert className="mt-3">
                <CircleCheck />
                <AlertTitle>Password updated</AlertTitle>
                <AlertDescription>
                  You can close this tab and return to the app.
                </AlertDescription>
              </Alert>
              <Button asChild className="mt-3 w-full">
                <Link to="/">Go to start page</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
